import { useTranslate } from "@tolgee/react";
import { Button } from "#/components/ui/button";
import { formatSignedPercent, formatSignedUsd } from "#/lib/formatters";

/**
 * Friendly logged-out invite card for /portfolio, following the fintech
 * "sample data before signup" pattern: a mock PnL card (clearly badged as a
 * sample) shows what the page turns into, a benefit-led headline replaces
 * the command-form prompt, and reassurance microcopy under the CTA names the
 * real cost of entry (email, ~30s — login is email-code only).
 */

// Illustrative sample values — static by design; no live data before login.
const SAMPLE_PNL_USD = 248.13;
const SAMPLE_PNL_PCT = 2.03;
const SAMPLE_SPARK = "0,20 12,16 24,18 36,11 48,13 60,7 72,9 84,3";

export function PortfolioLoginGate({
	onLogin,
	loginDisabled,
}: {
	onLogin: () => void;
	loginDisabled: boolean;
}) {
	const { t } = useTranslate();

	return (
		<div className="glass-card flex w-full max-w-sm flex-col items-center gap-5 rounded-3xl p-8 text-center ring-1 ring-border/40">
			<div className="w-full rounded-2xl bg-muted/50 px-4 py-3 text-left">
				<div className="flex items-center justify-between gap-3">
					<span className="text-caption1-medium text-muted-foreground">
						{t("portfolio.gate.sample-caption")}
					</span>
					<span className="rounded-full bg-background/90 px-2 py-0.5 text-caption2 text-muted-foreground">
						{t("portfolio.gate.sample-label")}
					</span>
				</div>
				<div className="mt-1 flex items-end justify-between gap-3">
					<div>
						<p className="text-title2 text-trade-green tabular-nums">
							{formatSignedUsd(SAMPLE_PNL_USD)}
						</p>
						<p className="text-caption1-tabular-medium text-trade-green">
							{formatSignedPercent(SAMPLE_PNL_PCT)}
						</p>
					</div>
					<svg
						viewBox="0 0 84 24"
						className="mb-1 h-6 w-21 shrink-0 overflow-visible"
						aria-hidden="true"
					>
						<defs>
							<linearGradient id="pf-gate-spark" x1="0" y1="0" x2="0" y2="1">
								<stop stopColor="var(--trade-green)" stopOpacity="0.25" />
								<stop
									offset="1"
									stopColor="var(--trade-green)"
									stopOpacity="0"
								/>
							</linearGradient>
						</defs>
						<path
							d="M0 20 L12 16 L24 18 L36 11 L48 13 L60 7 L72 9 L84 3 L84 24 L0 24 Z"
							fill="url(#pf-gate-spark)"
						/>
						<polyline
							points={SAMPLE_SPARK}
							fill="none"
							strokeWidth="1.5"
							strokeLinecap="round"
							strokeLinejoin="round"
							vectorEffect="non-scaling-stroke"
							className="stroke-trade-green"
						/>
					</svg>
				</div>
			</div>

			<div>
				<p className="text-headline text-foreground">
					{t("portfolio.gate.title")}
				</p>
				<p className="mt-1.5 text-body2 text-muted-foreground">
					{t("portfolio.gate.subtitle")}
				</p>
			</div>

			<div className="flex w-full flex-col items-center gap-2">
				<Button
					size="lg"
					className="w-full"
					onClick={onLogin}
					disabled={loginDisabled}
				>
					{t("actions.login")}
				</Button>
				<p className="text-caption1 text-muted-foreground">
					{t("portfolio.gate.microcopy")}
				</p>
			</div>
		</div>
	);
}
